import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Users, FileText, Receipt, Briefcase, Smartphone, Boxes, Rocket,
  CheckSquare, LifeBuoy, UserCog, Settings, Search, Sun, Moon, LogOut,
  Command, ChevronDown, Gauge, Mail, Phone, TrendingUp, Tv
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { CommandPalette } from "./CommandPalette";
import { useTenant } from "@/context/TenantContext";
import api from "@/lib/api";

const ADMIN_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { group: "Sales", perm: ["leads:read", "quotes:read", "customers:read"] },
  { to: "/leads", label: "Leads", icon: Sparkles, perm: "leads:read" },
  { to: "/quotes", label: "Quotes", icon: FileText, perm: "quotes:read" },
  { to: "/customers", label: "Customers", icon: Users, perm: "customers:read" },
  { group: "Delivery", perm: ["projects:read", "apps:read", "products:read"] },
  { to: "/projects", label: "Projects", icon: Briefcase, perm: "projects:read" },
  { to: "/apps", label: "Applications", icon: Smartphone, perm: "apps:read" },
  { to: "/products", label: "Software", icon: Boxes, perm: "products:read" },
  { to: "/revenue", label: "Revenue", icon: TrendingUp, perm: "revenue:read" },
  { to: "/tv", label: "TV mode", icon: Tv, adminOnly: true },
  { to: "/roadmap", label: "Roadmap", icon: Rocket, perm: "roadmap:read" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { group: "Finance & Support", perm: ["invoices:read", "tickets:read"] },
  { to: "/invoices", label: "Invoices", icon: Receipt, perm: "invoices:read" },
  { to: "/tickets", label: "Support", icon: LifeBuoy, perm: "tickets:read" },
  { group: "Workspace" },
  { to: "/team", label: "Team", icon: UserCog, adminOnly: true },
  { to: "/resource", label: "Resource", icon: Gauge, adminOnly: true },
  { to: "/profile", label: "Profile", icon: UserCog },
  { to: "/settings", label: "Settings", icon: Settings, adminOnly: true },
];

const CUSTOMER_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { group: "Delivery" },
  { to: "/my-projects", label: "Projects", icon: Briefcase },
  { to: "/my-apps", label: "Applications", icon: Smartphone },
  { to: "/my-software", label: "Software", icon: Boxes },
  { to: "/my-roadmap", label: "What's coming", icon: Rocket },
  { group: "Finance & Support" },
  { to: "/my-invoices", label: "Invoices", icon: Receipt },
  { to: "/my-quotes", label: "Quotes", icon: FileText },
  { to: "/my-tickets", label: "Support", icon: LifeBuoy },
  { group: "Account" },
  { to: "/profile", label: "Profile", icon: UserCog },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { tenant } = useTenant();
  const nav = useNavigate();
  const [cmdOpen, setCmdOpen] = useState(false);

  React.useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const isCustomer = user?.role === "customer";
  const { hasPerm } = useAuth();
  const isOwnerOrAdmin = user?.is_owner || user?.role === "admin";

  // Filter nav based on permissions for staff; owner/admin see everything; customer sees customer nav
  const navSource = isCustomer ? CUSTOMER_NAV : ADMIN_NAV;
  const items = React.useMemo(() => {
    if (isCustomer || isOwnerOrAdmin) return navSource;
    // Staff: hide items they can't read
    const filtered = [];
    for (let i = 0; i < navSource.length; i++) {
      const it = navSource[i];
      if (it.group) {
        // Check if any subsequent items (until next group) are visible
        const rest = [];
        for (let j = i + 1; j < navSource.length; j++) {
          if (navSource[j].group) break;
          rest.push(navSource[j]);
        }
        const anyVisible = rest.some((r) => {
          if (r.adminOnly) return false;
          if (!r.perm) return true;
          return hasPerm(r.perm);
        });
        if (anyVisible) filtered.push(it);
        continue;
      }
      if (it.adminOnly) continue;
      if (it.perm && !hasPerm(it.perm)) continue;
      filtered.push(it);
    }
    return filtered;
  }, [navSource, isCustomer, isOwnerOrAdmin, hasPerm]);

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))] text-[hsl(var(--text))]">
      {/* Sidebar */}
      <aside className="w-[248px] shrink-0 border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-5 h-[60px] flex items-center border-b border-[hsl(var(--border))]">
          <Logo />
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--text-subtle))]">
            {isCustomer ? "Customer" : (user?.is_owner || user?.role === "admin" ? "Owner" : (user?.team_member?.role_title || "Staff"))}
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {items.map((it, idx) => {
            if (it.group) {
              return (
                <div key={"g" + idx} className="px-3 pt-5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--text-subtle))]">
                  {it.group}
                </div>
              );
            }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  cn(
                    "group relative flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-all",
                    isActive
                      ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]"
                      : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] hover:bg-[hsl(var(--surface-hover))]"
                  )
                }
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {({ isActive }) => (
                  <>
                    <it.icon className="w-[17px] h-[17px]" strokeWidth={1.6} />
                    <span>{it.label}</span>
                    {isActive && <span className="absolute right-2 w-1 h-1 rounded-full bg-[hsl(var(--primary))]" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Sidebar footer: user */}
        <div className="p-3 border-t border-[hsl(var(--border))]">
          {isCustomer && <AccountManagerCard />}
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <img
              src={user?.avatar_url || "/brand/soubor-one-mark.png"}
              alt=""
              className="w-8 h-8 rounded-full object-cover ring-2 ring-[hsl(var(--border))]"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-[60px] border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]/80 backdrop-blur-md sticky top-0 z-20 flex items-center px-6 gap-4" data-testid="topbar">
          <button
            onClick={() => setCmdOpen(true)}
            className="flex items-center gap-2 px-3 h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] text-[13px] hover:border-[hsl(var(--border-strong))] transition-colors min-w-[280px]"
            data-testid="search-btn"
          >
            <Search className="w-4 h-4" strokeWidth={1.6} />
            <span>Search everything…</span>
            <span className="ml-auto flex items-center gap-1">
              <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[hsl(var(--surface-hover))]">⌘</kbd>
              <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[hsl(var(--surface-hover))]">K</kbd>
            </span>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggle}
              className="h-9 w-9 rounded-lg border border-[hsl(var(--border))] flex items-center justify-center hover:bg-[hsl(var(--surface-hover))]"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.6} /> : <Moon className="w-4 h-4" strokeWidth={1.6} />}
            </button>
            <button
              className="h-9 w-9 rounded-lg border border-[hsl(var(--border))] flex items-center justify-center relative hover:bg-[hsl(var(--surface-hover))] hover:border-[hsl(var(--primary))] transition-colors"
              onClick={() => nav(isCustomer ? "/my-tickets" : "/tickets")}
              title="Support"
              data-testid="topbar-support"
            >
              <LifeBuoy className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6} />
            </button>
            <div className="pl-2 ml-1 border-l border-[hsl(var(--border))] flex items-center gap-2 cursor-pointer hover:opacity-80" onClick={() => nav("/profile")} data-testid="topbar-profile">
              <img src={user?.avatar_url || "/brand/soubor-one-mark.png"} className="w-8 h-8 rounded-full object-cover" alt="" />
              <div className="text-[13px] font-medium hidden sm:block">{user?.name?.split(" ")[0]}</div>
              <ChevronDown className="w-3.5 h-3.5 text-[hsl(var(--text-muted))] hidden sm:block" strokeWidth={1.6} />
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8 overflow-x-hidden fade-in" data-testid="main-content">
          {children}
        </main>
      </div>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </div>
  );
}

function AccountManagerCard() {
  const [am, setAm] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/team/my-account-manager").then(({ data }) => setAm(data)).catch(() => setAm(null)).finally(() => setLoaded(true));
  }, []);
  if (!loaded || !am) return null;
  return (
    <div className="mb-3 p-3 rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))]" data-testid="account-manager-card">
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-2">Your account manager</div>
      <div className="flex items-center gap-2">
        <img src={am.avatar_url || "/brand/soubor-one-mark.png"} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-[hsl(var(--border))] shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold truncate">{am.name}</div>
          <div className="text-[10px] text-[hsl(var(--text-muted))] truncate">{am.role_title}</div>
        </div>
      </div>
      <div className="mt-2 flex gap-1">
        {am.email && <a href={`mailto:${am.email}`} className="flex-1 px-2 py-1 text-[10px] font-semibold rounded border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] inline-flex items-center justify-center gap-1 transition-colors" title={am.email} data-testid="am-email"><Mail className="w-3 h-3" strokeWidth={2}/>Email</a>}
        {am.phone && <a href={`tel:${am.phone}`} className="flex-1 px-2 py-1 text-[10px] font-semibold rounded border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] inline-flex items-center justify-center gap-1 transition-colors" title={am.phone} data-testid="am-phone"><Phone className="w-3 h-3" strokeWidth={2}/>Call</a>}
      </div>
    </div>
  );
}


export default Layout;
